import { useQuery } from '@apollo/client';
import { updateQueryString, OptionalString, parseQueryString } from '@sixfold/app-utils';
import { Stack, Heading, Box, Input } from '@sixfold/common-ui';
import { BackofficeScope } from '@sixfold/session-interface';
import { isNil } from '@sixfold/typed-primitives';
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { BulkAddVehiclesModal } from './bulk_add_vehicles_modal';
import { ManageVehicleModal } from './manage_vehicle_modal';
import { ManagedVehicleList, ManagedVehicleListProps } from './managed_vehicle_list';
import { InfiniteScroll } from '../../components/infinitescroll';
import { useIsAdmin, useHasScopes } from '../../lib/authorization';
import {
  ManagedVehicleListQuery,
  ManagedVehicleListQueryVariables,
  loadMoreFromConnection,
  getNodes,
  PAGINATION_PAGE_SIZE,
} from '../../lib/graphql';
import { Loader } from '../../lib/loader';
import { useDebounce } from '../../lib/util/debounce';
import { managedVehiclesQuery } from '../../vehicle/graphql';

class LoadingContainer extends Loader<
  ManagedVehicleListQuery,
  Omit<ManagedVehicleListProps, 'telematicsIntegrationId' | 'hasEditPermission'>,
  ManagedVehicleListQueryVariables
> {}

type Props = {
  telematics_integration_id: string;
};

export const ManagedVehicleListContainer: React.FunctionComponent<Props> = ({ telematics_integration_id }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const hasCompanyWritePermission = useHasScopes([BackofficeScope.companyWriteVehicles]);
  const hasEditPermission = isAdmin || hasCompanyWritePermission;

  const { license_plate } = parseQueryString({
    queryParametersToParse: {
      license_plate: OptionalString,
    },
    queryString: location.search,
  });
  const [licensePlate, setLicensePlate] = useState<string | undefined>(license_plate);
  const debouncedLicensePlate = useDebounce(licensePlate);

  const handleFilterToggle = useCallback(
    (filterType: string, filterValue?: string) => {
      return navigate(
        `${location.pathname}?${updateQueryString(location.search, {
          param: filterType,
          value: filterValue,
        })}`,
      );
    },
    [navigate, location.pathname, location.search],
  );

  useEffect(() => {
    const value = debouncedLicensePlate !== null ? debouncedLicensePlate : undefined;
    void handleFilterToggle('license_plate', value);
  }, [debouncedLicensePlate, handleFilterToggle]);

  const isValidLicensePlateSearch = !isNil(debouncedLicensePlate) && debouncedLicensePlate.trim().length >= 2;

  const result = useQuery<ManagedVehicleListQuery, ManagedVehicleListQueryVariables>(managedVehiclesQuery, {
    variables: {
      integrationIds: [telematics_integration_id],
      licensePlateSearch: isValidLicensePlateSearch ? debouncedLicensePlate : undefined,
      limit: PAGINATION_PAGE_SIZE,
    },
  });

  return (
    <Box>
      <Heading>Vehicles</Heading>
      <Stack justifyContent="space-between" alignItems="center">
        <Box paddingBlockStart="medium" paddingBlockEnd="medium">
          <Input
            name="license_plate"
            value={licensePlate}
            onChange={(e) => {
              setLicensePlate(e.value);
            }}
            label="License plate"
            labelPosition="inline"
          />
        </Box>
        {hasEditPermission && (
          <Stack direction="row" gap="small">
            <ManageVehicleModal telematicsIntegrationId={telematics_integration_id} trigger="Add vehicle" />
            <BulkAddVehiclesModal telematicsIntegrationId={telematics_integration_id} trigger="Add multiple vehicles" />
          </Stack>
        )}
      </Stack>
      <LoadingContainer
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        result={result as any}
        mapData={({ data }) => ({
          vehicles: getNodes(data.vehicles),
        })}>
        {(data) => (
          <InfiniteScroll
            triggerOnWindow={true}
            loadMoreEntries={() => loadMoreFromConnection(managedVehiclesQuery, result, ['vehicles'])}>
            <ManagedVehicleList
              hasEditPermission={hasEditPermission}
              vehicles={data.vehicles}
              telematicsIntegrationId={telematics_integration_id}
            />
          </InfiniteScroll>
        )}
      </LoadingContainer>
    </Box>
  );
};
